/** Состояние клиента и два стора: см. `../store.ts`. */
import type { BattleSnapshot, Landing, Locale, Platform, PlayerHistory, PlayerState } from "@newfungame/contracts";
import type { BoostOption } from "@newfungame/engine";
import { createStore, type Store } from "./create-store";
import type { Route } from "./routes";

export interface Insets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export type AuthStatus = "idle" | "pending" | "ready" | "error";

/** Почему бой стоит (кроме окон): свёрнуто приложение. */
export type PauseReason = "hidden" | "map";

export interface AppState {
  locale: Locale;
  platform: Platform;
  /** Открыто как Telegram Mini App. */
  miniApp: boolean;
  startParam: string | null;
  /** UTM и внешний referrer этого захода — серверу для источника нового игрока. */
  landing: Landing | null;
  auth: {
    status: AuthStatus;
    /** Гость в браузере (без Telegram). */
    guest: boolean;
    /** Аккаунт создан этим входом — новичок: никаких окон на старте, сразу бой. */
    created: boolean;
    /** Код последней ошибки входа (`ApiErrorCode` или `network`). */
    error: string | null;
    /** Что было у игрока на любом устройстве; `null` — ещё не вошли. */
    history: PlayerHistory | null;
  };
  /** Подтверждённое сервером состояние игрока; `null` — ещё не вошли. */
  player: PlayerState | null;
  /** Предложение «1 из 3» после пройденной волны; `null` — нет. Заполняет бой (I1), показывает окно C4. */
  boostOffer: BoostOption[] | null;
  route: Route;
  /** Причины паузы, кроме окон. Пусто — бой может идти. */
  pause: PauseReason[];
  /**
   * Отступы Mini App на полном экране (вырез, панели и кнопки Telegram), px — то же, что CSS `--app-inset-*`.
   * Поле и интерфейс отступают на них (в браузере ещё и на `env(safe-area-inset-*)`, см. `AppController.attachField`).
   */
  insets: Insets;
  /**
   * Счётчик забежек на клиенте: растёт, когда сервер закрыл забежку (выход после 10 мин в фоне). Бой (I1) по смене
   * начинает новую забежку с `player.run`.
   */
  runEpoch: number;
  /** Разница часов сервера и клиента, мс: таймер показывается по часам сервера. */
  clockOffset: number;
  /** Выбранная скорость боя; действует только у VIP. */
  speedPref: BattleSpeed;
  /** Автонавыки включены; действует только у VIP. */
  autoPref: boolean;
  /** Монеты волн, уже прибавленные к `player.coins`, но ещё не зачисленные сервером (журнал в пути). */
  coinsInFlight: number;
  /**
   * Сервер ответил на покупку «не хватает монет», когда у него было столько монет. Пока тратимых больше не стало,
   * кнопки покупок погашены с «Не хватает монет»; `null` — отказа не было.
   */
  coinsShort: number | null;
}

export type BattleSpeed = 1 | 2 | 5 | 10;
/** Шаги кнопки скорости админа; сервер принимает журналы до x10 только у него. */
export const ADMIN_SPEEDS: readonly BattleSpeed[] = [1, 2, 5, 10];

export function initialAppState(over: Partial<AppState> = {}): AppState {
  return {
    locale: "en",
    platform: "browser",
    miniApp: false,
    startParam: null,
    landing: null,
    auth: { status: "idle", guest: true, created: false, error: null, history: null },
    player: null,
    boostOffer: null,
    route: { windows: [], tab: null },
    pause: [],
    insets: { top: 0, bottom: 0, left: 0, right: 0 },
    runEpoch: 0,
    clockOffset: 0,
    speedPref: 1,
    autoPref: false,
    coinsInFlight: 0,
    coinsShort: null,
    ...over,
  };
}

export const appStore: Store<AppState> = createStore(initialAppState());
export const battleStore: Store<BattleSnapshot | null> = createStore<BattleSnapshot | null>(null);

export function patch(fn: (s: AppState) => Partial<AppState>, store: Store<AppState> = appStore): void {
  store.set((s) => ({ ...s, ...fn(s) }));
}
